/**
 * The SINGLE conversion point for stats units (S-ODL-64): the database/BE
 * work EXCLUSIVELY in canonical metric units (`canonicalUnit` from the
 * registry), while display/input honor the user's `userSettings` choices.
 *
 * Canonical unit → userSettings dimension mapping rules:
 *  - `kg`   → `weight` (kg | lb | oz)
 *  - `kcal` → `energy` (kcal | kj)
 *  - `L`    → `volume` (l | oz — fluid ounce)
 *  - `m`    → `length` (km | mi)
 *  - `cm`   → `height` (cm | mm | ft; cm = identity), but ONLY for the
 *    `height` metric; body circumferences stay in cm (ft/mm make no sense
 *    for circumference, and no other setting option exists).
 *  - everything else (bpm, %, g, mg, min, ms, mmHg, mmol/L, ml/kg/min, kg/m2,
 *    deg, count, unitless) — no conversion.
 */
import { isDefined } from '@mfw/common';

import type { ApiMetricDefinition } from '../api/types/stats.types';
import type { ApiUserSettings } from '../schemas/users.schema';

export type MetricUnitSettings = Partial<
  Pick<ApiUserSettings, 'energy' | 'height' | 'length' | 'volume' | 'weight'>
> | null;

type MetricDefLike = Pick<
  ApiMetricDefinition,
  'canonicalUnit' | 'key' | 'valueKind'
>;

const KG_TO_OZ = 35.27396195;
const KG_TO_LB = 2.2046226218;
const KCAL_TO_KJ = 4.184;
const L_TO_FL_OZ = 33.8140227;
const M_TO_KM = 0.001;
const M_TO_MI = 1 / 1609.344;
const CM_TO_MM = 10;
const CM_TO_FT = 1 / 30.48;

type UnitResolution = {
  /** unit for display (label alongside the value) */
  unit: null | string;
  /** canonical value × factor = display value */
  factor: number;
};

function resolve(
  def: MetricDefLike,
  settings: MetricUnitSettings,
): UnitResolution {
  const canonical = def.canonicalUnit;
  const noop = { factor: 1, unit: canonical };
  if (!canonical || !settings) {
    return noop;
  }
  switch (canonical) {
    case 'kg':
      if (settings.weight === 'lb') return { factor: KG_TO_LB, unit: 'lb' };
      return settings.weight === 'oz' ? { factor: KG_TO_OZ, unit: 'oz' } : noop;
    case 'kcal':
      return settings.energy === 'kj'
        ? { factor: KCAL_TO_KJ, unit: 'kJ' }
        : noop;
    case 'L':
      return settings.volume === 'oz'
        ? { factor: L_TO_FL_OZ, unit: 'fl oz' }
        : noop;
    case 'm':
      if (settings.length === 'mi') {
        return { factor: M_TO_MI, unit: 'mi' };
      }
      return settings.length === 'km' ? { factor: M_TO_KM, unit: 'km' } : noop;
    case 'cm':
      if (def.key !== 'height') {
        return noop;
      }
      if (settings.height === 'ft') {
        return { factor: CM_TO_FT, unit: 'ft' };
      }
      return settings.height === 'mm' ? { factor: CM_TO_MM, unit: 'mm' } : noop;
    default:
      return noop;
  }
}

/** Unit in which the metric is DISPLAYED for the given user. */
export function displayUnit(def: MetricDefLike, settings: MetricUnitSettings) {
  return resolve(def, settings).unit;
}

/** Canonical value (from the BE) → value in the user's unit. */
export function toDisplay(
  value: number,
  def: MetricDefLike,
  settings: MetricUnitSettings,
) {
  return value * resolve(def, settings).factor;
}

/** Value entered in the user's unit → canonical (for sending to the BE). */
export function toCanonical(
  value: number,
  def: MetricDefLike,
  settings: MetricUnitSettings,
) {
  return value / resolve(def, settings).factor;
}

/**
 * Formatted display of a value with its unit: precision by `valueKind`
 * (scale/integer → whole number; number → up to 2 decimals, no trailing zeros).
 */
export function formatMetricValue(
  value: null | number | undefined,
  def: MetricDefLike,
  settings: MetricUnitSettings,
  { withUnit = true }: { withUnit?: boolean } = {},
) {
  if (!isDefined(value) || !Number.isFinite(value)) {
    return '—';
  }
  const { factor, unit } = resolve(def, settings);
  const display = value * factor;
  const maximumFractionDigits = def.valueKind === 'number' ? 2 : 0;
  const formatted = new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
  }).format(display);
  return withUnit && unit ? `${formatted} ${unit}` : formatted;
}
